import { useCallback, useEffect, useRef, useState } from "react";
import { chatStream, fetchAIWelcome, fetchAIHistory, fetchAIConversation } from "../../services/aiApi";

/**
 * useAIChat — state machine for the Brixi AI chat panel.
 *
 * One message = { id, role, content, toolCalls[], toolResults[], createdAt }
 * Streaming text appends to the current assistant message in-place.
 */
export function useAIChat({ active = true } = {}) {
  const [welcome, setWelcome] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState("New chat");
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const bootstrapped = useRef(false);

  // Welcome and history are not needed until the panel is opened.
  useEffect(() => {
    if (!active || bootstrapped.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [nextWelcome, nextHistory] = await Promise.all([
          fetchAIWelcome(),
          fetchAIHistory(),
        ]);
        if (cancelled) return;
        bootstrapped.current = true;
        setWelcome(nextWelcome);
        setHistory(nextHistory);
      } catch (e) {
        // welcome can fail silently — chat still works
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  // Listen for external "send this prompt" trigger (e.g. clicking a CTA elsewhere)
  useEffect(() => {
    const handler = (e) => {
      const prompt = e?.detail?.prompt;
      if (prompt) send(prompt);
    };
    window.addEventListener("brixi-ai:send", handler);
    return () => window.removeEventListener("brixi-ai:send", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await fetchAIHistory());
    } catch { /* ignore */ }
  }, []);

  const openConversation = useCallback(async (id) => {
    try {
      const conv = await fetchAIConversation(id);
      setConversationId(conv.id);
      setConversationTitle(conv.title || "Chat");
      setMessages(conv.messages || []);
      setError(null);
    } catch (e) {
      setError(e?.message || "Could not open conversation.");
    }
  }, []);

  const newConversation = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setConversationId(null);
    setConversationTitle("New chat");
    setMessages([]);
    setError(null);
  }, []);

  const send = useCallback(
    async (text, context = {}) => {
      if (!text?.trim() || streaming) return;
      setError(null);
      const trimmed = text.trim();

      // Build a clean body — never send null/empty extras the backend might reject.
      const body = { message: trimmed, stream: true };
      if (conversationId) body.conversation_id = conversationId;
      if (context && Object.keys(context).length) body.context = context;

      const userMsg = {
        id: `local-user-${Date.now()}`,
        role: "user",
        content: trimmed,
        toolCalls: [],
        toolResults: [],
        createdAt: new Date().toISOString(),
      };
      const assistantMsg = {
        id: `local-asst-${Date.now()}`,
        role: "assistant",
        content: "",
        toolCalls: [],
        toolResults: [],
        createdAt: new Date().toISOString(),
        streaming: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      await chatStream(
        body,
        {
          abortSignal: controller.signal,
          onEvent: (type, data) => {
            switch (type) {
              case "session":
                if (data?.conversation_id) setConversationId(data.conversation_id);
                if (data?.title) setConversationTitle(data.title);
                break;
              case "text":
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") {
                    last.content = (last.content || "") + (data?.delta || "");
                  }
                  return next;
                });
                break;
              case "tool_call":
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") {
                    last.toolCalls = [
                      ...(last.toolCalls || []),
                      { id: data?.id, name: data?.name, arguments: data?.arguments, status: "running" },
                    ];
                  }
                  return next;
                });
                break;
              case "tool_result":
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") {
                    last.toolCalls = (last.toolCalls || []).map((tc) =>
                      tc.id === data?.tool_call_id
                        ? { ...tc, status: data?.ok ? "ok" : "error", preview: data?.preview, error: data?.error, latencyMs: data?.latency_ms }
                        : tc
                    );
                  }
                  return next;
                });
                break;
              case "error":
                setError(data?.message || "Something went wrong.");
                break;
              case "done":
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") last.streaming = false;
                  return next;
                });
                break;
              default:
                break;
            }
          },
          onError: (e) => setError(e?.message || "Connection error."),
          onClose: () => {
            setStreaming(false);
            abortRef.current = null;
            refreshHistory();
          },
        }
      );
    },
    [conversationId, streaming, refreshHistory]
  );

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setStreaming(false);
    }
  }, []);

  return {
    welcome,
    conversationId,
    conversationTitle,
    messages,
    history,
    streaming,
    error,
    send,
    stop,
    newConversation,
    openConversation,
    refreshHistory,
  };
}
